import React from 'react';
import NumberFormat from 'react-number-format';
import { LineItem } from 'lib/types/invoices';
import { formatPrice } from 'lib/pricing/ui';
import { RefundDisplayItem } from './types';

// Light currency validation: allow digits plus optional single decimal point with up to 2 decimals.
const isValidCurrencyInput = (value: string) =>
  /^\d*(?:\.\d{0,2})?$/.test(value);

/**
 * Shared normalization helper so change & blur logic stay in sync.
 * finalize=false (onChange): soft validation, ignore invalid or exceeding keystrokes.
 * finalize=true (onBlur): clamp to max and format to 2 decimals; empty -> 0.00.
 */
function computeNextRefundInput(
  raw: string,
  maxAmountInCents: number,
  finalize: boolean
): string | null {
  if (raw === '') {
    return finalize ? '0.00' : '';
  }
  if (!isValidCurrencyInput(raw)) {
    return null; // ignore invalid pattern
  }
  const numeric = parseFloat(raw);
  if (isNaN(numeric)) {
    return null;
  }
  const maxDollars = maxAmountInCents / 100;
  if (!finalize) {
    if (numeric > maxDollars) {
      return null; // soft guard, don't mutate
    }
    return raw; // preserve user typing (e.g. trailing '.')
  }
  const clamped = Math.min(numeric, maxDollars);
  return clamped.toFixed(2);
}

interface InvoiceRefundModalOrderTableProps {
  currency: string;
  displayItems: RefundDisplayItem[];
  perItemInputs: Record<string, string>;
  onPerItemInputChange: (id: string, value: string) => void;
  refunds?: LineItem[] | null;
  totalRefundedInCentsFromPriorInvoiceTransactions?: number; // includes prior refunds already applied
}

export function InvoiceRefundModalOrderTable({
  currency,
  displayItems,
  perItemInputs,
  onPerItemInputChange,
  refunds,
  totalRefundedInCentsFromPriorInvoiceTransactions = 0
}: InvoiceRefundModalOrderTableProps) {
  const handleChange = (key: string, raw: string, maxAmountInCents: number) => {
    const next = computeNextRefundInput(raw, maxAmountInCents, false);
    if (next === null) {
      return;
    }
    onPerItemInputChange(key, next);
  };

  const handleBlur = (key: string, raw: string, maxAmountInCents: number) => {
    const next = computeNextRefundInput(raw, maxAmountInCents, true);
    if (next === null) {
      return;
    }
    onPerItemInputChange(key, next);
  };

  const hasRefunds = !!refunds && refunds.length > 0;
  const totalPriorRefundedDisplay = formatPrice(
    currency,
    totalRefundedInCentsFromPriorInvoiceTransactions
  );
  return (
    <div className="text-sm text-column-gray-600 space-y-2">
      <div
        className="grid grid-cols-12 gap-2 font-medium text-column-gray-500 pb-1 border-b border-column-gray-100"
        role="row"
      >
        <div className="col-span-7" id="refund-publication-details-header">
          Publication Details
        </div>
        <div
          className="col-span-2 text-right"
          id="refund-original-amount-header"
        >
          Original Amount
        </div>
        <div className="col-span-3 text-right" id="refund-amount-header">
          Refund Amount
        </div>
      </div>
      {displayItems.map((item, idx) => {
        const key = item.id || String(idx);
        const inputVal = perItemInputs[key] ?? '0.00';
        const maxAmountInCents = item.amountInCents;
        return (
          <div
            key={key}
            className="grid grid-cols-12 items-center gap-2 border-b border-column-gray-50 py-2"
          >
            <div className="col-span-7 h-full">
              <div
                className="truncate h-full flex items-center"
                title={item.description}
              >
                {item.description}
              </div>
            </div>
            <div className="col-span-2 text-right">
              <NumberFormat
                value={(item.amountInCents / 100).toFixed(2)}
                thousandSeparator
                prefix={currency}
                decimalScale={2}
                fixedDecimalScale
                displayType="text"
              />
            </div>

            <div className="col-span-3">
              <input
                id={`refund-item-${key}`}
                aria-labelledby="refund-amount-header"
                className="w-full border border-column-gray-100 rounded px-2 py-1 text-sm text-right"
                type="text"
                inputMode="decimal"
                value={inputVal}
                onChange={e =>
                  handleChange(key, e.target.value, maxAmountInCents)
                }
                onBlur={e => handleBlur(key, e.target.value, maxAmountInCents)}
                placeholder="0.00"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>
        );
      })}
      {hasRefunds && (
        <div className="mt-4 border border-column-gray-100 rounded p-3 bg-column-green-50 text-column-green-600">
          <div className="font-medium mb-1">Prior Refunds</div>
          <ul className="space-y-1">
            {refunds.map((r, i) => (
              <li key={i} className="flex justify-between text-xs">
                <span className="truncate" title={r.description}>{`Refund ${
                  i + 1
                }: ${r.description}`}</span>
                <NumberFormat
                  value={formatPrice(currency, -1 * r.amount)}
                  thousandSeparator
                  prefix={currency}
                  decimalScale={2}
                  fixedDecimalScale
                  displayType="text"
                />
              </li>
            ))}
          </ul>
          <div className="flex justify-between text-xs mt-2 font-semibold text-column-green-500">
            <span>Total Prior Refunds</span>
            <NumberFormat
              value={totalPriorRefundedDisplay}
              thousandSeparator
              prefix={currency}
              decimalScale={2}
              fixedDecimalScale
              displayType="text"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceRefundModalOrderTable;
